// packages/shared/src/constants/functionalAreas.ts
// Functional area taxonomy and ICD-10 inference for geriatric/functional rehab templates.
// Parallel concept to body regions — drives conditional visibility, prefill, and smart text filtering.

/** All supported functional areas with display labels */
export const FUNCTIONAL_AREAS: { id: string; label: string; description?: string }[] = [
  { id: 'balance_mobility', label: 'Balance / Mobility / Fall Prevention', description: 'Gait, balance, falls, coordination, dizziness' },
  { id: 'ue_le', label: 'UE / LE Functional Use', description: 'Upper and lower extremity functional impairment' },
  { id: 'pain', label: 'Pain Management', description: 'Pain, tenderness, stiffness, loss of function' },
  { id: 'swallowing', label: 'Swallowing', description: 'Dysphagia, aphagia, swallowing disorders' },
  { id: 'adl', label: 'Activities of Daily Living', description: 'Self-care, mobility assistance, supervision needs' },
  { id: 'speech_language_cognition', label: 'Speech Language & Cognition', description: 'Aphasia, dysarthria, cognitive-communication, memory' },
  { id: 'concussion_vestibular', label: 'Concussion / Vestibular', description: 'Post-concussion, vestibular, dizziness, oculomotor' },
];

/**
 * ICD-10 prefix → functional area mapping.
 * Matched by longest prefix first. A single code can map to multiple areas
 * (e.g., R26.2 → balance_mobility + adl), so inference collects all matches.
 * Sources: ICD-10-CM symptom codes (R-chapter), generalized musculoskeletal,
 * Z-codes, cognitive/speech codes (F/I chapters).
 */
const ICD10_FUNCTIONAL_AREA_MAP: [string, string][] = [
  // Balance / Mobility / Fall Prevention
  ['R26.0', 'balance_mobility'],   // Ataxic gait
  ['R26.1', 'balance_mobility'],   // Paralytic gait
  ['R26.2', 'balance_mobility'],   // Difficulty in walking
  ['R26.81', 'balance_mobility'],  // Unsteadiness on feet
  ['R26.89', 'balance_mobility'],  // Other abnormalities of gait and mobility
  ['R29.6', 'balance_mobility'],   // Repeated falls
  ['R29.898', 'balance_mobility'], // Other symptoms involving nervous and musculoskeletal systems
  ['R42', 'balance_mobility'],     // Dizziness and giddiness
  ['R27.0', 'balance_mobility'],   // Ataxia, unspecified
  ['R27.8', 'balance_mobility'],   // Other lack of coordination
  ['M62.81', 'balance_mobility'],  // Muscle weakness (generalized)
  ['R53.1', 'balance_mobility'],   // Weakness
  ['R53.83', 'balance_mobility'],  // Other fatigue

  // Concussion / Vestibular (R42 dual-mapped with balance)
  ['S06.0', 'concussion_vestibular'],   // Concussion
  ['F07.81', 'concussion_vestibular'],  // Postconcussional syndrome
  ['H81', 'concussion_vestibular'],     // Disorders of vestibular function
  ['H83.0', 'concussion_vestibular'],   // Labyrinthitis
  ['R42', 'concussion_vestibular'],     // Dizziness and giddiness

  // Swallowing (Dysphagia)
  ['R13.0', 'swallowing'],    // Aphagia
  ['R13.10', 'swallowing'],   // Dysphagia, unspecified
  ['R13.11', 'swallowing'],   // Dysphagia, oral phase
  ['R13.12', 'swallowing'],   // Dysphagia, oropharyngeal phase
  ['R13.13', 'swallowing'],   // Dysphagia, pharyngeal phase
  ['R13.14', 'swallowing'],   // Dysphagia, pharyngoesophageal phase
  ['R13.19', 'swallowing'],   // Other dysphagia

  // Activities of Daily Living (dual-mapped codes also appear above)
  ['R26.2', 'adl'],      // Difficulty in walking
  ['R26.89', 'adl'],     // Other gait/mobility abnormalities
  ['R29.6', 'adl'],      // Repeated falls
  ['Z74.0', 'adl'],      // Need for assistance due to reduced mobility
  ['Z74.1', 'adl'],      // Need for assistance with personal care
  ['Z74.2', 'adl'],      // Need for assistance at home
  ['Z74.3', 'adl'],      // Need for continuous supervision
  ['R41.89', 'adl'],     // Other symptoms involving cognitive functions

  // Speech Language & Cognition
  ['R47.01', 'speech_language_cognition'],  // Aphasia
  ['R47.02', 'speech_language_cognition'],  // Dysphasia
  ['R47.1', 'speech_language_cognition'],   // Dysarthria and anarthria
  ['R47.81', 'speech_language_cognition'],  // Slurred speech
  ['R47.89', 'speech_language_cognition'],  // Other speech disturbances
  ['R48.0', 'speech_language_cognition'],   // Dyslexia and alexia
  ['R48.1', 'speech_language_cognition'],   // Agnosia
  ['R48.2', 'speech_language_cognition'],   // Apraxia
  ['R48.8', 'speech_language_cognition'],   // Other symbolic dysfunctions
  ['R41.0', 'speech_language_cognition'],   // Disorientation, unspecified
  ['R41.1', 'speech_language_cognition'],   // Anterograde amnesia
  ['R41.2', 'speech_language_cognition'],   // Retrograde amnesia
  ['R41.3', 'speech_language_cognition'],   // Other amnesia
  ['R41.840', 'speech_language_cognition'], // Attention and concentration deficit
  ['R41.841', 'speech_language_cognition'], // Cognitive communication deficit
  ['R41.89', 'speech_language_cognition'],  // Other symptoms involving cognitive functions
  ['R41.9', 'speech_language_cognition'],   // Unspecified cognitive symptoms
  ['F80.0', 'speech_language_cognition'],   // Phonological disorder
  ['F80.1', 'speech_language_cognition'],   // Expressive language disorder
  ['F80.2', 'speech_language_cognition'],   // Mixed receptive-expressive language disorder
  ['I69.320', 'speech_language_cognition'], // Aphasia following cerebral infarction
  ['I69.321', 'speech_language_cognition'], // Dysphasia following cerebral infarction
  ['I69.323', 'speech_language_cognition'], // Fluency disorder following cerebral infarction
  ['I69.328', 'speech_language_cognition'], // Other speech/language deficits following cerebral infarction
];

/**
 * Infer functional areas from an array of ICD-10 codes.
 * Uses longest-prefix matching against the functional area map.
 * Unlike body regions (one code → one region), a single code can map to
 * multiple functional areas (e.g., R26.2 → balance_mobility + adl).
 *
 * @param codes Array of ICD-10 code objects (must have a `code` string property)
 * @returns Deduplicated array of functional area IDs
 */
export function inferFunctionalAreasFromICD10(
  codes: { code: string; [key: string]: unknown }[]
): string[] {
  if (!codes || codes.length === 0) return [];

  const sortedMap = [...ICD10_FUNCTIONAL_AREA_MAP].sort(
    (a, b) => b[0].length - a[0].length
  );

  const areas = new Set<string>();

  for (const { code } of codes) {
    if (!code) continue;
    const upper = code.toUpperCase();
    // Collect the longest-match prefix length for this code, then add all areas
    // that match at that same prefix length (handles dual-mapped codes).
    let bestLen = 0;
    const matches: string[] = [];
    for (const [prefix, area] of sortedMap) {
      if (prefix.length < bestLen) break;
      if (upper.startsWith(prefix.toUpperCase())) {
        bestLen = prefix.length;
        matches.push(area);
      }
    }
    for (const a of matches) areas.add(a);
  }

  return [...areas];
}
